import {
	AddressTypeV4Enum,
	AddressV4ItemRequest,
	FavoritePlace,
	PredefinedFavoritePlacesEnum,
	PostBeneficiaryAddressSuggestionV4Request,
} from 'domain/usecases';
import {AddressFormV2Values} from 'presentation/components/ChangeData/Address/AddressFormV2/types';
import {formatMasks, getAddressByGroups} from 'presentation/utils';
import {resolveStreetTypeFromZipCodeLookup} from 'presentation/components/ChangeData/Address/hooks/utils';
import {LatLng} from './types';

function nmLocalToAddressTypeV4(nmLocal: string | undefined, type?: AddressTypeV4Enum): number {
	if (nmLocal === PredefinedFavoritePlacesEnum.HOUSE) return AddressTypeV4Enum.RESIDENTIAL;
	if (nmLocal === PredefinedFavoritePlacesEnum.WORK) return AddressTypeV4Enum.COMMERCIAL;
	if (type === AddressTypeV4Enum.BILLING) return AddressTypeV4Enum.BILLING;
	return AddressTypeV4Enum.OTHERS;
}

const formatCoordinate = (value: string | number) => {
	const coordinate = Number(value);

	if (!Number.isFinite(coordinate)) {
		return '';
	}

	return coordinate.toFixed(7);
};

function formValuesToAddressV4Item(
	values: AddressFormV2Values,
	latLng?: LatLng | null,
): AddressV4ItemRequest {
	const {addressType, addressName} = getAddressByGroups(values.address);
	const type = nmLocalToAddressTypeV4(values.favoriteAddress, values.type);
	const unmaskedZipCode = formatMasks('clear', values.zipCode);
	const resolvedStreetType = resolveStreetTypeFromZipCodeLookup(addressType, unmaskedZipCode);

	const item: AddressV4ItemRequest = {
		type: type,
		zipCode: unmaskedZipCode,
		state: values.state.trim(),
		city: values.city.trim(),
		district: values.district.trim(),
		street: addressName.trim(),
		streetType: (resolvedStreetType ?? addressType).trim(),
		streetNumber: values.withoutNumber ? '0' : String(Number(values.number) || 0),
		complement: values.complement.trim(),
	};

	if (latLng != null) {
		const lat = formatCoordinate(latLng.latitude);
		const lng = formatCoordinate(latLng.longitude);
		if (lat) item.latitude = lat;
		if (lng) item.longitude = lng;
	}

	if (type === AddressTypeV4Enum.OTHERS && values.favoriteAddress) {
		item.descriptionOthers = values.favoriteAddress;
	}

	return item;
}

function favoritePlaceToAddressV4Item(
	address: FavoritePlace,
	dependentCodes?: string[],
): AddressV4ItemRequest {
	const {addressType, addressName} = getAddressByGroups(address.nmLogradouro);
	const type = nmLocalToAddressTypeV4(address.nmLocal, address.type);

	const item: AddressV4ItemRequest = {
		type,
		zipCode: address.nuCep,
		state: address.nmUf,
		city: address.nmCidade,
		district: address.nmBairro,
		street: addressName,
		streetType: addressType,
		streetNumber: String(address.nuEndereco),
		complement: address.dsComplemento ?? '',
	};

	if (address.cdLatitude) item.latitude = formatCoordinate(address.cdLatitude);
	if (address.cdLongitude) item.longitude = formatCoordinate(address.cdLongitude);

	if (type === AddressTypeV4Enum.OTHERS) {
		item.descriptionOthers = address.nmLocal;
	}

	if (dependentCodes && dependentCodes.length > 0) {
		item.dependentCodes = dependentCodes;
	}

	return item;
}
function favoritePlaceToAddressSuggestionV4Payload(
	address: FavoritePlace,
	personCode: string[],
): Omit<PostBeneficiaryAddressSuggestionV4Request, 'signal'> {
	const {addressType, addressName} = getAddressByGroups(address.nmLogradouro);

	const payload: Omit<PostBeneficiaryAddressSuggestionV4Request, 'signal'> = {
		personCode,
		type: AddressTypeV4Enum.RESIDENTIAL,
		zipCode: address.nuCep,
		state: address.nmUf,
		city: address.nmCidade,
		district: address.nmBairro,
		street: addressName,
		streetType: addressType,
		streetNumber: String(address.nuEndereco),
		complement: address.dsComplemento ?? '',
	};

	if (address.cdLatitude) payload.latitude = formatCoordinate(address.cdLatitude);
	if (address.cdLongitude) payload.longitude = formatCoordinate(address.cdLongitude);

	return payload;
}

export {
	nmLocalToAddressTypeV4,
	formValuesToAddressV4Item,
	favoritePlaceToAddressV4Item,
	favoritePlaceToAddressSuggestionV4Payload,
};
export type {LatLng};
